/**
 * cardUtils.js
 * Utility functions for generating consistent, vibrant card thumbnails
 * based on unit/course names. Designed as a fallback until actual
 * images/Automated-generated thumbnails are supported.
 *
 * Future roadmap:
 *   - Institution admin can upload a custom image per course/unit
 *   - Admins can trigger Automated image generation during course creation
 *   - This gradient will remain the fallback when no imageUrl is set
 */
import React from 'react';
import { getImageUrl } from './imageUtils';

const GRADIENTS = [
  { from: 'from-violet-600', to: 'to-indigo-700', text: 'VL' },
  { from: 'from-orange-500', to: 'to-pink-600',   text: 'OR' },
  { from: 'from-cyan-500',   to: 'to-blue-600',   text: 'CY' },
  { from: 'from-emerald-500',to: 'to-teal-700',   text: 'EM' },
  { from: 'from-rose-500',   to: 'to-pink-700',   text: 'RS' },
  { from: 'from-amber-500',  to: 'to-orange-600', text: 'AM' },
  { from: 'from-blue-600',   to: 'to-violet-700', text: 'BL' },
  { from: 'from-fuchsia-500',to: 'to-purple-700', text: 'FC' },
  { from: 'from-lime-500',   to: 'to-emerald-600',text: 'LM' },
  { from: 'from-sky-500',    to: 'to-indigo-600', text: 'SK' },
  { from: 'from-red-500',    to: 'to-rose-700',   text: 'RD' },
  { from: 'from-teal-500',   to: 'to-cyan-700',   text: 'TL' },
];

/**
 * Returns a deterministic gradient class pair for a given name string.
 * The same name always maps to the same gradient.
 *
 * @param {string} name - Course or unit name
 * @returns {{ from: string, to: string }} Tailwind gradient class pair
 */
export const getCardGradient = (name = '') => {
  const hash = name
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return GRADIENTS[hash % GRADIENTS.length];
};

/**
 * Returns up to 2 uppercase initials for a name.
 * e.g. "Introduction to Python" → "IP", "Homiletics" → "HO"
 *
 * @param {string} name - Course or unit name
 * @returns {string} 1–2 character initials string
 */
export const getCardInitials = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Renders a gradient thumbnail <div> with large initials overlay.
 * Use this to build the top thumbnail section of each card.
 *
 * Example usage:
 *   const { from, to } = getCardGradient(unit.name);
 *   const initials = getCardInitials(unit.name);
 *   <div className={`aspect-video bg-gradient-to-br ${from} ${to}`}>
 *     <span>{initials}</span>
 *   </div>
 */
export const CardThumbnail = ({ name, imageUrl, className = '' }) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const resolvedImageUrl = getImageUrl(imageUrl);
  const hasImage = resolvedImageUrl && resolvedImageUrl !== 'null' && resolvedImageUrl !== 'undefined' && !imageFailed;

  if (hasImage) {
    return (
      <div className={`w-full aspect-video relative overflow-hidden bg-gray-100 ${className}`}>
        <img 
          src={resolvedImageUrl} 
          alt={name} 
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          onError={(e) => {
            setImageFailed(true);
          }}
        />
        <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>
      </div>
    );
  }

  const { from, to } = getCardGradient(name);
  const initials = getCardInitials(name);

  return (
    <div className={`w-full aspect-video bg-gradient-to-br ${from} ${to} flex items-center justify-center relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]"></div>
      <span className="text-white/90 text-4xl font-black tracking-tighter opacity-50 select-none relative z-10">
        {initials}
      </span>
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent"></div>
    </div>
  );
};
